import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  ATTEMPT_DERIVED_STATUS,
  BUTTON_VARIANTS,
  COLORS,
  DAY_TIME_INTERVALS_HOURS,
  EMAIL_ENTITY_TYPE,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { EmailTemplateType } from "@miplatform/types";
import { getTimeOfDay } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiRefresh } from "@mdi/js";
import { updateRecipientName } from "./util/emailFormUtil";
import { SPECIFIED_TIME } from "./EmailTemplate/emailTemplateConstants";
import { EmailUserAutocomplete } from "./EmailUserAutocomplete/EmailUserAutocomplete";
import { EMAIL_TEMPLATE_TYPE_TO_DRAWER_CONFIG } from "./emailFormConstants";
import { useEmailFormViewModel } from "./useEmailFormViewModel";

const emailInformationSchema = z.object({
  details: z.string().min(1, "Details are required."),
  contactInformation: z.string().min(1, "Contact information is required."),
});

export const useEmailForm = ({
  open = true,
  entity,
  entityType = EMAIL_ENTITY_TYPE.ATTEMPT,
  step,
  attempt,
  templateType,
  onClose = () => {},
  onCloseoutDrawerOpen = () => {},
}) => {
  const [isPreviewOpen, handlePreviewOpen, handlePreviewClose] = useBoolean();
  const [isConfirmModalOpen, handleConfirmModalOpen, handleConfirmModalClose] =
    useBoolean();
  const [files, setFiles] = useState([]);
  const openCloseoutRef = useRef(false);

  const {
    title,
    recipient,
    detailsLabel,
    modalDescription,
    color,
    hasArrivalWindow,
    getSubject,
    getDetails,
    getAdditionalNotes,
    getContactInformation,
  } = EMAIL_TEMPLATE_TYPE_TO_DRAWER_CONFIG[templateType];
  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { isValid },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      subject: getSubject(entity, SPECIFIED_TIME),
      details: "",
      contactInformation: "",
      additionalNotes: "",
      arrivalWindow: SPECIFIED_TIME,
      to: [],
      cc: [],
    },
    resolver: zodResolver(emailInformationSchema),
  });
  const arrivalWindowOptions = useMemo(() => {
    if (!hasArrivalWindow || !entity?.scheduledStartDate) {
      return [];
    }

    const timeOfDay = getTimeOfDay(entity.scheduledStartDate);

    return [
      { label: SPECIFIED_TIME, value: SPECIFIED_TIME },
      {
        label: `${timeOfDay} (${DAY_TIME_INTERVALS_HOURS[timeOfDay].start} - ${DAY_TIME_INTERVALS_HOURS[timeOfDay].end})`,
        value: timeOfDay,
      },
    ];
  }, [entity]);

  const handleClose = () => {
    handlePreviewClose();
    handleConfirmModalClose();
    onClose();
  };

  const {
    isLoading,
    isSent,
    account,
    user,
    contacts,
    documentCategory,
    entity: viewModelEntity,
    home,
    getAccount,
    getUser,
    getEmailContacts,
    getDocumentCategoryOptions,
    getEmailHistory,
    getEntity,
    sendEmail,
    getHome,
    attachments,
    hasCameraLine,
    hasCameraDocuments,
    cameraLineDocuments,
    selectedCameraLineDocumentIds,
    handleCameraLineDocumentToggle,
  } = useEmailFormViewModel({
    entity,
    entityType,
    step,
    attempt,
    templateType,
    files,
    openCloseoutRef,
    getValues,
    onClose: handleClose,
    onCloseoutDrawerOpen,
  });

  const to = watch("to");
  const cc = watch("cc");
  const buttonLabel = isSent ? "Resend" : "Send";
  const isSubmitDisabled =
    !isValid ||
    isLoading ||
    to.length === 0 ||
    to.some((item) => item.isError) ||
    cc.some((item) => item.isError) ||
    (Array.isArray(files) && files.some((file) => file.error));

  const emailFields = [
    {
      label: "Subject",
      name: "subject",
      formControl: control,
      width: !hasArrivalWindow ? "100%" : "50%",
    },
    {
      label: "Arrival Window",
      name: "arrivalWindow",
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      width: "50%",
      options: arrivalWindowOptions,
      style: !hasArrivalWindow && { display: "none" },
    },
    {
      label: "To",
      name: "to",
      fieldType: INPUT_TYPES.CUSTOM,
      formControl: control,
      width: "50%",
      customComponent: (
        <EmailUserAutocomplete
          label="To"
          data-testId="mi-autocomplete-to"
          options={contacts?.relatedContacts || []}
          onChange={(value) => setValue("to", value)}
          value={to}
        />
      ),
    },
    {
      label: "Cc",
      name: "cc",
      fieldType: INPUT_TYPES.CUSTOM,
      formControl: control,
      width: "50%",
      customComponent: (
        <EmailUserAutocomplete
          label="Cc"
          data-testId="mi-autocomplete-cc"
          options={contacts?.relatedContacts || []}
          onChange={(value) => setValue("cc", value)}
          value={cc}
        />
      ),
    },
    {
      label: detailsLabel,
      name: "details",
      required: true,
      formControl: control,
      multiline: true,
      rows: 4,
      width: "100%",
    },
    {
      label: "Additional Notes (Optional)",
      name: "additionalNotes",
      formControl: control,
      multiline: true,
      rows: 3,
      width: "100%",
    },
    {
      label: "Contact Information",
      name: "contactInformation",
      required: true,
      formControl: control,
      multiline: true,
      rows: 6,
      width: "50%",
    },
  ];

  const handleFileChange = (files) => {
    setFiles(files);
  };

  const actions = (() => {
    const color = isSent ? COLORS.ORANGE : COLORS.PRIMARY;
    const startIcon = isSent && { path: mdiRefresh };

    const actions = [
      {
        name: "Cancel",
        variant: BUTTON_VARIANTS.TEXT,
        color: COLORS.BLACK,
        onClick: handleClose,
      },
    ];

    if (
      (templateType === EmailTemplateType.CloseoutAndCompletionNotice &&
        viewModelEntity?.derivedStatus ===
          ATTEMPT_DERIVED_STATUS.PENDING_CLOSEOUT) ||
      viewModelEntity?.derivedStatus === ATTEMPT_DERIVED_STATUS.PENDING_PREBILL
    ) {
      actions.push({
        name: `${buttonLabel} Email & Close`,
        variant: BUTTON_VARIANTS.OUTLINED,
        startIcon,
        color,
        disabled: isSubmitDisabled,
        onClick: handleConfirmModalOpen,
      });
      if (viewModelEntity?.assignment?.order?.isPrebill) {
        actions.push({
          name: `${buttonLabel} Email & Pre-Bill`,
          variant: BUTTON_VARIANTS.CONTAINED,
          startIcon,
          color,
          disabled: isSubmitDisabled,
          onClick: () => {
            handleConfirmModalOpen();
            openCloseoutRef.current = true;
          },
        });
      }

      return actions;
    }

    actions.push({
      name: `${buttonLabel} ${title}`,
      variant: BUTTON_VARIANTS.CONTAINED,
      startIcon,
      color,
      disabled: isSubmitDisabled,
      onClick: handleConfirmModalOpen,
    });

    return actions;
  })();

  useEffect(() => {
    if (user) {
      setValue("contactInformation", getContactInformation(user), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [user]);

  useEffect(() => {
    const values = getValues();
    reset({
      ...values,
      subject: getSubject(viewModelEntity, SPECIFIED_TIME),
      to: contacts?.to || [],
      cc: contacts?.cc || [],
      details: getDetails(
        contacts?.to || [],
        viewModelEntity,
        values.arrivalWindow
      ),
      additionalNotes: getAdditionalNotes?.(entity, home) || "",
      contactInformation: getContactInformation(user),
    });
  }, [user, home, contacts, viewModelEntity, templateType]);

  useEffect(() => {
    const values = getValues();
    setValue("details", updateRecipientName(values.details, to));
  }, [to]);

  useEffect(() => {
    if (open) {
      getEmailHistory();
      getEmailContacts({ id: entity.id, templateType, entity });
      setValue("additionalNotes", getAdditionalNotes?.(entity, home) || "");
    }
  }, [open, home, entity, templateType]);

  useEffect(() => {
    getAccount();
    getUser();
    getHome();
    getDocumentCategoryOptions();
  }, []);

  useEffect(() => {
    getEntity();
  }, [entity]);

  return {
    entity: viewModelEntity,
    isPreviewOpen,
    isConfirmModalOpen,
    isLoading,
    isSent,
    title,
    recipient,
    color,
    buttonLabel,
    isSubmitDisabled,
    emailFields,
    files,
    actions,
    templateType,
    documentCategory,
    account,
    user,
    modalDescription,
    getValues,
    attachments,
    hasCameraLine,
    hasCameraDocuments,
    cameraLineDocuments,
    selectedCameraLineDocumentIds,
    handleCameraLineDocumentToggle,
    handlePreviewOpen,
    handlePreviewClose,
    handleConfirmModalOpen,
    handleConfirmModalClose,
    handleFileChange,
    handleClose,
    sendEmail,
  };
};
